import React, {KeyboardEventHandler, ReactNode} from 'react'
import {COMMON_CONSTANTS} from '../../../../CommonConstants'
import {Nullable} from '../../../../app/TypeHelpers'

export interface EnterKeyHandlerProps {
    onKeyPress?: KeyboardEventHandler<Element>;
}
interface Props {
    children?: ReactNode
}
export const withEnterKeyHandler = <OriginalProps extends {}>(
    WrappedComponent: React.ComponentType<OriginalProps & EnterKeyHandlerProps>,
) => {

    type ResultProps = OriginalProps & Props;

    const isEnterKeyPressed = (event: React.KeyboardEvent<HTMLInputElement>): boolean => {
        return event.which === COMMON_CONSTANTS.ENTER_KEY_CODE
    }

    const isVisibleElement = (item: HTMLElement): boolean => {
        if (!item) {
            return false
        }
        return (item.clientHeight !== 0 && item.clientWidth !== 0)
    }

    const TINY_MCE_COMPONENT_ID_PREFIX: string = 'tiny-react_'
    type FocusChangeableInputElement = HTMLInputElement | HTMLTextAreaElement
    const findInputElementsInSameForm = (form: HTMLFormElement) => {
        return Array.from(form)
            .filter(item => (item instanceof HTMLInputElement || item instanceof HTMLTextAreaElement))
            .map(item => (item as FocusChangeableInputElement))
            .filter(item => (!item.disabled))
            .filter(item => item.type !== 'file')
            .filter(item => item.type !== 'checkbox')
            .filter(item => item.type !== 'radio')
            .filter(item => item.id !== 'selectCondominium')

            .filter(item => {
                if (isVisibleElement(item)) {
                    return true
                }
                const itemId: string = item.id
                const itemIsTinyMce: boolean = itemId.startsWith(TINY_MCE_COMPONENT_ID_PREFIX)
                return itemIsTinyMce
            })
    }

    const TINY_MCE_COMPONENT_IFRAME_ID_POSTFIX: string = '_ifr'
    const findTinyMceComponent = (id: string): Nullable<HTMLBodyElement> => {
        const TINY_MCE_IFRAME_ID = `${id}${TINY_MCE_COMPONENT_IFRAME_ID_POSTFIX}`
        const iframeWindow: HTMLIFrameElement = document.getElementById(TINY_MCE_IFRAME_ID) as HTMLIFrameElement
        if (!iframeWindow) {
            return null
        }
        const contentWindow = iframeWindow.contentWindow
        if (!contentWindow) {
            return null
        }
        const TINY_MCE_SELECTOR: string = `body[id=tinymce][data-id=${id}]`
        const tinyMceComponent = contentWindow.document.querySelector(TINY_MCE_SELECTOR) as Nullable<HTMLBodyElement>
        if (!tinyMceComponent) {
            return null
        }
        if (isVisibleElement(tinyMceComponent)) {
            return tinyMceComponent
        }
        return null
    }

    const returnTinyMceComponentOrAfterNext = (
        nextInput: FocusChangeableInputElement, nextIndexInForms: number, formInputs: FocusChangeableInputElement[],
    ): HTMLBodyElement | FocusChangeableInputElement => {
        const mceComponent = findTinyMceComponent(nextInput.id)
        if (mceComponent) {
            return mceComponent
        }
        const afterNextIndexInForms = (nextIndexInForms + 1) % formInputs.length
        return formInputs[afterNextIndexInForms]
    }

    type FocusChangeableInputFormOrHtmlEditor = FocusChangeableInputElement | HTMLBodyElement
    const findNextFocusInputForm = (
        currentInput: FocusChangeableInputElement, formInputs: FocusChangeableInputElement[],
    ): FocusChangeableInputFormOrHtmlEditor => {
        const currentIndexInFormInputs = formInputs.indexOf(currentInput)
        const nextIndexInForms = (currentIndexInFormInputs + 1) % formInputs.length
        const nextInput = formInputs[nextIndexInForms]

        if (nextInput.id.startsWith(TINY_MCE_COMPONENT_ID_PREFIX)) {
            return returnTinyMceComponentOrAfterNext(nextInput, nextIndexInForms, formInputs)
        }

        return nextInput
    }

    const focusNextInput = (form: HTMLFormElement, currentInput: FocusChangeableInputElement) => {
        const formInputs: FocusChangeableInputElement[] = findInputElementsInSameForm(form)
        const nextFocusElement: HTMLElement = findNextFocusInputForm(currentInput, formInputs)
        nextFocusElement.focus()
        return true
    }

    const isFileSelectionInput = (input: HTMLInputElement): boolean => {
        return input.type === 'file'
    }

    const EnterKeyHandler: React.FC<ResultProps> = (props) => {
        const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
            if (!isEnterKeyPressed(event)) {
                return
            }

            if (!(event.target instanceof HTMLInputElement)) {
                return
            }

            if (isFileSelectionInput(event.target)) {
                return
            }

            if (event.target.form === null) {
                return
            }

            if (focusNextInput(event.target.form, event.target)) {
                event.preventDefault()
                return
            }
        }

        return (
            <WrappedComponent {...props} onKeyPress={handleKeyDown}>
                {props.children}
            </WrappedComponent>
        )
    }
    EnterKeyHandler.displayName = 'withEnterKeyHandler'
    return EnterKeyHandler
}
